/**
 * Query-param / cookie feature-flag overrides for testing flags in-environment
 * (including production), where a flag can't otherwise be resolved ON — e.g. the
 * anonymous-projects flow keys its flag on an anonymous id that never matches
 * the internal-email cohort, so internal users can't drive the flow in prod
 * without this.
 *
 * `?flags=roadmap` on the page enables `task-roadmap`. A client effect persists
 * it to the `lev_flag_overrides` cookie so it survives navigation AND rides
 * along on API requests (which never see the page's query string) — that cookie
 * is what the server honors.
 *
 * SECURITY: `?flags=` is reachable by ANY visitor in production, so this is a
 * strict ALLOWLIST, not a general override like NEXT_PUBLIC_FEATURE_FLAG_OVERRIDES.
 * Only flags in OVERRIDABLE_FLAGS can ever be forced on — anything else in the
 * value is ignored. Only add rollout/UX flags with no security or cost blast
 * radius (never labs, kill switches, or paid features).
 *
 * Dependency-free and non-"use client" so both the client hook
 * (src/lib/analytics/featureFlags.ts) and the server helper
 * (src/lib/featureFlags/server.ts) can import it.
 */
import { FEATURE_FLAGS } from "@/lib/analytics/featureFlagKeys";

/** Cookie the client persists the resolved override into; the server reads it. */
export const FLAG_OVERRIDE_COOKIE = "lev_flag_overrides";
/** Query-param on the page that seeds the cookie, e.g. `?flags=roadmap`. */
export const FLAG_OVERRIDE_QUERY_PARAM = "flags";

/**
 * The ONLY real flag keys `?flags=` / the cookie may force-enable. Keep minimal.
 */
const OVERRIDABLE_FLAGS: ReadonlySet<string> = new Set<string>([
  // `?flags=mobile-app` on a phone sends you to the mobile shell AND opens the
  // `/m` routes, which `notFound()` on `isMobileAppEnabled` — and that helper
  // honors this same cookie. Production-blocked below: without it, any
  // signed-in prod visitor could open the whole unreleased surface with a query
  // param, and turning the flag off in PostHog would not close it for anyone
  // still holding the 4h cookie, so it would not be a kill switch. Staging and
  // preview keep the escape hatch, which is where the team and the e2e spec
  // use it.
  FEATURE_FLAGS.MOBILE_APP,
  // The team self-enables the roadmap via `?flags=roadmap` on staging/preview.
  // NOT honored in production (see PRODUCTION_BLOCKED_OVERRIDES): the server gate
  // honors this same cookie, so enabling the roadmap authorizes completion-ledger
  // writes + weekly-report activities + structural events — an external prod
  // visitor must not be able to bypass the Internal Users cohort with it.
  FEATURE_FLAGS.TASK_ROADMAP,
  // The Overall Evaluation narrative (an improvement narrative string + the
  // one-founder/one-Lev `doer` pair). `?flags=eval-tasks-narrative` forces the
  // narrative schema/prompt on client AND server (isEvalNarrativeEnabled
  // honors the cookie). Production-blocked below: the ON state swaps in a
  // different prompt/schema and real LLM cost per evaluation — team/dev/staging
  // testing only.
  FEATURE_FLAGS.EVAL_TASKS_NARRATIVE,
  // Input-hash evaluation reuse. `?flags=eval-stable-results` forces it on
  // client AND server (isEvalStableResultsEnabled honors the cookie).
  // Production-blocked below: the ON state can SKIP a fresh model call and
  // serve a stored evaluation instead — a correctness-affecting behavior
  // change, not just UX, so an arbitrary prod visitor must not be able to
  // force it ahead of rollout.
  FEATURE_FLAGS.EVAL_STABLE_RESULTS,
  // The whole anonymous-projects flow. `?flags=anon` (or the literal key) forces
  // it on client AND server: the client `useFeatureFlag` hook and the
  // `isAnonymousProjectsEnabled` server helper both honor this cookie, so the
  // mint/auth API accepts the visitor instead of 403ing under the override. A
  // logged-out visitor's email can never match the flag's release condition, so
  // this cookie is the only way to drive the flow while signed out. Deliberately
  // NOT production-blocked: the team drives the real prod flow via `?flags=`
  // ahead of the rollout, and the feature is about to ship to everyone anyway.
  FEATURE_FLAGS.ANONYMOUS_PROJECTS,
  // The conscious note-taker manual-completion flow. `?flags=task-notes-v3`
  // (or `?flags=notes-v3`) forces it on client AND server (the cookie rides API
  // requests, so `isTaskNotesV3Enabled` honors it too). Production-blocked below:
  // the ON state runs LevAI question/eval/proposal generation and writes a
  // DocumentVersion on accept — real cost + write blast radius an arbitrary prod
  // visitor must not trigger. Team/dev/staging testing only.
  FEATURE_FLAGS.TASK_NOTES_V3,
  // Routing Lev-directed task interaction to the side chat. `?flags=task-lev-to-chat`
  // (or `?flags=lev-to-chat`) forces it on client AND server — `isTaskLevToChatEnabled`
  // honors the cookie, so the composer redirect and the addComment gate agree.
  // Production-blocked below: the split is driven on staging/preview only.
  FEATURE_FLAGS.TASK_LEV_TO_CHAT,
  // The pipeline Kanban bulk-select + bulk action bar (Delete, Move to).
  // `?flags=pipeline-bulk` forces it on client AND server — `isPipelineBulkActionsEnabled`
  // honors the cookie, so the checkbox/bulk-bar UI and the bulk mutations
  // agree. Production-blocked below: bulk delete is a real destructive write
  // an arbitrary prod visitor must not be able to unlock ahead of rollout.
  FEATURE_FLAGS.PIPELINE_BULK_ACTIONS,
  // proto-blocks: QA dev override only. Production-blocked below (creates PROTO blocks
  // and pre-render Inngest jobs on project creation — blast radius in prod).
  FEATURE_FLAGS.PROTO_BLOCKS,
  // The redesigned logged-out homepage. `?flags=home-v2` forces it on (client
  // only; there is no server gate). Deliberately NOT production-blocked: pure
  // visual change with no write or cost blast radius, previewed in prod.
  FEATURE_FLAGS.LANDING_HOME_V2,
  // The tasks-v4 umbrella (stage-as-rung + evidence completion). `?flags=tasks-v4`
  // forces it on client AND server — `isTasksV4Enabled` honors the cookie, so the
  // client UI and the server generator/advance gates agree.
  // Production-blocked below: the ON state runs background LLM task generation +
  // structural writes an arbitrary prod visitor must not trigger. Team/dev/staging
  // testing only.
  FEATURE_FLAGS.TASKS_V4,
  // The Customer Discovery row rebuild. `?flags=customer-discovery-v2` forces
  // it on client AND server — `readCustomerDiscoveryV2Verdict` honors the
  // cookie, so a project created under the override gets the v2 row stamp.
  // Production-blocked below: the ON state changes which tiles a NEW project
  // gets at creation (a structural write), matching the tasks-v4 reasoning —
  // team/dev/staging testing only.
  FEATURE_FLAGS.CUSTOMER_DISCOVERY_V2,
  // The ICP survey lightbox. `?flags=icp-survey` forces it on (client only;
  // there is no server gate). Deliberately NOT production-blocked: it is a
  // different view over the same ICP mutations, with no new write or cost.
  FEATURE_FLAGS.CUSTOMER_DISCOVERY_V2_ICP_SURVEY,
  // Industry and buyer words on the ICP survey. `?flags=industry-keywords`
  // forces it on client AND server — `isIcpIndustryKeywordsEnabled` honors
  // the cookie, so the extra step and the Apollo keyword tags agree.
  // Production-blocked below: the ON state changes the people search (Apollo
  // credits), so a prod visitor must not be able to force it.
  FEATURE_FLAGS.CUSTOMER_DISCOVERY_V2_INDUSTRY_KEYWORDS,
  // Hold task generation until the canvas is complete + reviewed.
  // `?flags=tasks-after-canvas` (or `?flags=after-canvas`) forces it on the
  // client and on the tRPC gates (getCurrentWeek, generate, replenish,
  // generateV4Tasks). It cannot reach the Inngest freeflow job's A3 choke, the
  // document-generation lifecycle, or the after-response dispatch: the cookie
  // is read from the current request, so those fall back to PostHog. QA the
  // full ON path with the dev PostHog flag, not this cookie. Production-
  // blocked below: the ON state changes WHEN background task generation
  // dispatches (a write/cost boundary), matching the tasks-v4 reasoning.
  FEATURE_FLAGS.TASKS_AFTER_CANVAS,
  // Progress updates on open human tasks. `?flags=task-progress-sync` (or
  // `?flags=progress-sync`) forces it on client AND server —
  // `isTaskProgressSyncEnabled` honors the cookie, so the composer copy and
  // the classify-and-draft path agree. Production-blocked below: the ON state
  // runs a model call per update and drafts canvas edits — cost a prod visitor
  // must not be able to trigger ahead of rollout.
  FEATURE_FLAGS.TASK_PROGRESS_SYNC,
  // The Facts debug inspector panel. `?flags=facts` forces it on (client
  // only; the route gates on sign-in itself, not this flag). Deliberately
  // NOT production-blocked: the flag only changes what the viewer sees, and
  // the route serves only the caller's own cookie session either way.
  FEATURE_FLAGS.FACTS_DEBUG_INSPECTOR,
]);

/**
 * Overrides that must NEVER be honored in production, because enabling the flag
 * has a real server-side blast radius (writes/side effects), not just UX. The
 * `?flags=` escape hatch stays available on staging/preview for the team.
 */
const PRODUCTION_BLOCKED_OVERRIDES: ReadonlySet<string> = new Set<string>([
  FEATURE_FLAGS.MOBILE_APP,
  FEATURE_FLAGS.TASK_ROADMAP,
  FEATURE_FLAGS.EVAL_TASKS_NARRATIVE,
  FEATURE_FLAGS.EVAL_STABLE_RESULTS,
  FEATURE_FLAGS.TASK_NOTES_V3,
  FEATURE_FLAGS.TASK_LEV_TO_CHAT,
  FEATURE_FLAGS.PIPELINE_BULK_ACTIONS,
  FEATURE_FLAGS.PROTO_BLOCKS,
  FEATURE_FLAGS.TASKS_V4,
  FEATURE_FLAGS.CUSTOMER_DISCOVERY_V2,
  FEATURE_FLAGS.CUSTOMER_DISCOVERY_V2_INDUSTRY_KEYWORDS,
  FEATURE_FLAGS.TASKS_AFTER_CANVAS,
  FEATURE_FLAGS.TASK_PROGRESS_SYNC,
]);

/** True in the production app environment (server: APP_ENV; client: NEXT_PUBLIC_APP_ENV). */
const IS_PRODUCTION =
  (process.env.APP_ENV ?? process.env.NEXT_PUBLIC_APP_ENV) === "production";

/** Short public aliases → real flag key, so `?flags=roadmap` reads nicely. */
const FLAG_ALIASES: Readonly<Record<string, string>> = {
  roadmap: FEATURE_FLAGS.TASK_ROADMAP,
  anon: FEATURE_FLAGS.ANONYMOUS_PROJECTS,
  "notes-v3": FEATURE_FLAGS.TASK_NOTES_V3,
  "lev-to-chat": FEATURE_FLAGS.TASK_LEV_TO_CHAT,
  "pipeline-bulk": FEATURE_FLAGS.PIPELINE_BULK_ACTIONS,
  "proto-blocks": FEATURE_FLAGS.PROTO_BLOCKS,
  proto: FEATURE_FLAGS.PROTO_BLOCKS,
  "home-v2": FEATURE_FLAGS.LANDING_HOME_V2,
  "after-canvas": FEATURE_FLAGS.TASKS_AFTER_CANVAS,
  "icp-survey": FEATURE_FLAGS.CUSTOMER_DISCOVERY_V2_ICP_SURVEY,
  "industry-keywords": FEATURE_FLAGS.CUSTOMER_DISCOVERY_V2_INDUSTRY_KEYWORDS,
  "progress-sync": FEATURE_FLAGS.TASK_PROGRESS_SYNC,
  facts: FEATURE_FLAGS.FACTS_DEBUG_INSPECTOR,
};

/**
 * Resolve a comma-separated `?flags=` / cookie value into the set of REAL flag
 * keys it enables. Accepts either the alias (`roadmap`) or the literal key
 * (`task-roadmap`); anything not on the allowlist is dropped.
 */
export function resolveFlagOverrides(raw: string | null | undefined): Set<string> {
  const enabled = new Set<string>();
  if (!raw) return enabled;
  for (const token of raw.split(",")) {
    const t = token.trim().toLowerCase();
    if (!t) continue;
    const flag = FLAG_ALIASES[t] ?? t;
    if (!OVERRIDABLE_FLAGS.has(flag)) continue;
    // Drop production-blocked overrides in prod so `?flags=` can't bypass a
    // server-consequential rollout; still allowed on staging/preview/dev.
    if (IS_PRODUCTION && PRODUCTION_BLOCKED_OVERRIDES.has(flag)) continue;
    enabled.add(flag);
  }
  return enabled;
}

/** Is `flag` force-enabled by the given raw `?flags=` / cookie value? */
export function isFlagForceEnabled(
  flag: string,
  raw: string | null | undefined,
): boolean {
  return resolveFlagOverrides(raw).has(flag);
}
